/**
 * LLM Result Truncator
 *
 * Utilities for truncating query and external tool results before passing to LLM.
 * Ensures data is compact while preserving essential information for analysis.
 */

import { safeTruncate, stripLoneSurrogates } from '../utils/surrogate';

// Default configuration
const DEFAULT_MAX_ROWS = 10;
const DEFAULT_MAX_CHARS_PER_FIELD = 500;

/**
 * Field type enumeration for schema inference
 */
export type FieldType = 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object' | 'null' | 'unknown';

/**
 * Schema field information
 */
export interface SchemaField {
	name: string;
	type: FieldType;
	truncated?: boolean;
}

/**
 * Options for truncating data
 */
export interface TruncateOptions {
	maxRows?: number;
	maxCharsPerField?: number;
}

/**
 * Formatted result structure for LLM consumption
 */
export interface FormattedResultForLLM {
	summary: {
		totalRecords: number;
		recordsShown: number;
		schema: SchemaField[];
	};
	metadata?: Record<string, unknown>; // Top-level non-array properties (e.g., totalItems, totalDeadstockItems)
	data: Record<string, unknown>[];
	truncationNote: string | null;
}

/**
 * Options for formatting external tool results
 */
export interface FormatToolResultOptions extends TruncateOptions {
	toolName?: string;
	toolLimit?: number; // Limit specified by the external tool itself
}

/**
 * Infers the type of a field value
 * @param value - The value to infer type from
 * @returns The inferred field type
 */
export function inferFieldType(value: unknown): FieldType {
	if (value === null || value === undefined) {
		return 'null';
	}

	if (typeof value === 'string') {
		// Check if it looks like a date
		if (isDateString(value)) {
			return 'date';
		}
		return 'string';
	}

	if (typeof value === 'number') {
		return 'number';
	}

	if (typeof value === 'boolean') {
		return 'boolean';
	}

	if (Array.isArray(value)) {
		return 'array';
	}

	if (typeof value === 'object') {
		return 'object';
	}

	return 'unknown';
}

/**
 * Checks if a string value looks like a date
 * @param value - The string value to check
 * @returns True if the string appears to be a date
 */
export function isDateString(value: string): boolean {
	// Common date patterns
	const datePatterns = [
		/^\d{4}-\d{2}-\d{2}$/,                          // YYYY-MM-DD
		/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/,         // ISO 8601
		/^\d{2}\/\d{2}\/\d{4}$/,                        // MM/DD/YYYY
		/^\d{4}\/\d{2}\/\d{2}$/,                        // YYYY/MM/DD
	];

	return datePatterns.some(pattern => pattern.test(value));
}

/**
 * Truncates a text field to specified length with indicator
 * @param value - The string value to truncate
 * @param maxLength - Maximum allowed length
 * @returns Truncated string with indicator if truncated
 */
export function truncateTextField(value: string, maxLength: number): { text: string; wasTruncated: boolean } {
	if (value.length <= maxLength) {
		// Sanitize even when not truncating: the cell may already carry lone
		// surrogates from corrupt source data (Excel/CSV/DB) that would break
		// the JSON request body.
		return { text: stripLoneSurrogates(value), wasTruncated: false };
	}

	// Surrogate-aware cut: never split a UTF-16 pair (which would emit a lone
	// surrogate that breaks JSON.stringify → invalid LLM request body).
	const truncated = safeTruncate(value, maxLength);
	const remaining = value.length - truncated.length;

	return {
		text: `${stripLoneSurrogates(truncated)}... (${remaining} more chars)`,
		wasTruncated: true
	};
}

/**
 * Truncates a single field value based on its type
 * @param value - The field value to truncate
 * @param maxCharsPerField - Maximum characters for text fields
 * @returns Object with truncated value and truncation status
 */
export function truncateFieldValue(
	value: unknown,
	maxCharsPerField: number
): { value: unknown; wasTruncated: boolean } {
	// Keep null/undefined as-is
	if (value === null || value === undefined) {
		return { value, wasTruncated: false };
	}

	// Keep numbers and booleans intact - needed for analysis
	if (typeof value === 'number' || typeof value === 'boolean') {
		return { value, wasTruncated: false };
	}

	// Truncate strings
	if (typeof value === 'string') {
		const result = truncateTextField(value, maxCharsPerField);
		return { value: result.text, wasTruncated: result.wasTruncated };
	}

	// For arrays, show count and first few items
	if (Array.isArray(value)) {
		if (value.length === 0) {
			return { value: [], wasTruncated: false };
		}

		const preview = value.slice(0, 3);
		const hasMore = value.length > 3;

		return {
			value: hasMore ? `[${preview.join(', ')}... (${value.length} items)]` : value,
			wasTruncated: hasMore
		};
	}

	// For objects, stringify and truncate
	if (typeof value === 'object') {
		const jsonStr = JSON.stringify(value);
		const result = truncateTextField(jsonStr, maxCharsPerField);
		return { value: result.text, wasTruncated: result.wasTruncated };
	}

	// Default: convert to string and truncate
	const strValue = String(value);
	const result = truncateTextField(strValue, maxCharsPerField);
	return { value: result.text, wasTruncated: result.wasTruncated };
}

/**
 * Truncates all fields in a single row
 * @param row - The data row to truncate
 * @param maxCharsPerField - Maximum characters for text fields
 * @returns Object with truncated row and set of truncated field names
 */
export function truncateRow(
	row: Record<string, unknown>,
	maxCharsPerField: number
): { row: Record<string, unknown>; truncatedFields: Set<string> } {
	const truncatedRow: Record<string, unknown> = {};
	const truncatedFields = new Set<string>();

	for (const [key, value] of Object.entries(row)) {
		const result = truncateFieldValue(value, maxCharsPerField);
		truncatedRow[key] = result.value;

		if (result.wasTruncated) {
			truncatedFields.add(key);
		}
	}

	return { row: truncatedRow, truncatedFields };
}

/**
 * Extracts metadata (non-array top-level properties) from an object result
 * This preserves important aggregate values like totalItems, totalCount, etc.
 * @param obj - The object to extract metadata from
 * @param dataKey - The key containing the data array (to exclude from metadata)
 * @param maxCharsPerField - Maximum characters for text fields
 * @returns Object with metadata and truncated fields
 */
export function extractMetadataFromObject(
	obj: Record<string, unknown>,
	dataKey: string,
	maxCharsPerField: number
): { metadata: Record<string, unknown>; truncatedFields: Set<string> } {
	const metadata: Record<string, unknown> = {};
	const truncatedFields = new Set<string>();

	for (const [key, value] of Object.entries(obj)) {
		// Skip the data array key
		if (key === dataKey) {
			continue;
		}

		// Skip arrays (we only want scalar metadata)
		if (Array.isArray(value)) {
			continue;
		}

		// Truncate and add to metadata
		const result = truncateFieldValue(value, maxCharsPerField);
		metadata[key] = result.value;

		if (result.wasTruncated) {
			truncatedFields.add(key);
		}
	}

	return { metadata, truncatedFields };
}

/**
 * Extracts schema information from data array
 * @param data - Array of data records
 * @param truncatedFields - Set of field names that were truncated
 * @returns Array of schema field information
 */
export function extractSchema(
	data: Record<string, unknown>[],
	truncatedFields: Set<string> = new Set()
): SchemaField[] {
	if (!data || data.length === 0) {
		return [];
	}

	// Use first row to extract field names and types
	const firstRow = data[0];
	const schema: SchemaField[] = [];

	for (const [name, value] of Object.entries(firstRow)) {
		schema.push({
			name,
			type: inferFieldType(value),
			truncated: truncatedFields.has(name) ? true : undefined
		});
	}

	return schema;
}

/**
 * Truncates an array of data records
 * @param data - Array of data records to truncate
 * @param options - Truncation options
 * @returns Object with truncated data and metadata
 */
export function truncateDataArray(
	data: Record<string, unknown>[],
	options: TruncateOptions = {}
): {
	data: Record<string, unknown>[];
	totalRecords: number;
	recordsShown: number;
	truncatedFields: Set<string>;
} {
	const maxRows = options.maxRows ?? DEFAULT_MAX_ROWS;
	const maxCharsPerField = options.maxCharsPerField ?? DEFAULT_MAX_CHARS_PER_FIELD;

	if (!data || !Array.isArray(data)) {
		return {
			data: [],
			totalRecords: 0,
			recordsShown: 0,
			truncatedFields: new Set()
		};
	}

	const totalRecords = data.length;
	const rowsToProcess = data.slice(0, maxRows);
	const truncatedData: Record<string, unknown>[] = [];
	const allTruncatedFields = new Set<string>();

	for (const row of rowsToProcess) {
		const { row: truncatedRow, truncatedFields } = truncateRow(row, maxCharsPerField);
		truncatedData.push(truncatedRow);

		// Merge truncated fields
		for (const field of truncatedFields) {
			allTruncatedFields.add(field);
		}
	}

	return {
		data: truncatedData,
		totalRecords,
		recordsShown: truncatedData.length,
		truncatedFields: allTruncatedFields
	};
}

/**
 * Builds a truncation note describing what was truncated
 * @param totalRecords - Total number of records in original data
 * @param recordsShown - Number of records included
 * @param truncatedFields - Set of field names that were truncated
 * @param maxCharsPerField - Max chars per field setting
 * @param sourceName - Optional source name (tool name or 'query')
 * @returns Human-readable truncation note or null if nothing truncated
 */
export function buildTruncationNote(
	totalRecords: number,
	recordsShown: number,
	truncatedFields: Set<string>,
	maxCharsPerField: number,
	sourceName?: string
): string | null {
	const parts: string[] = [];

	// Record truncation
	if (totalRecords > recordsShown) {
		const source = sourceName ? ` from ${sourceName}` : '';
		parts.push(`Showing ${recordsShown} of ${totalRecords} total records${source}`);
	}

	// Field truncation
	if (truncatedFields.size > 0) {
		const fieldList = Array.from(truncatedFields).join(', ');
		parts.push(`Fields truncated to ${maxCharsPerField} chars: ${fieldList}`);
	}

	return parts.length > 0 ? parts.join('. ') + '.' : null;
}

/**
 * Formats query result for LLM consumption
 * @param data - Raw query result data
 * @param options - Truncation options
 * @returns Formatted result structure for LLM
 */
export function formatQueryResultForLLM(
	data: unknown,
	options: TruncateOptions = {}
): FormattedResultForLLM {
	const maxCharsPerField = options.maxCharsPerField ?? DEFAULT_MAX_CHARS_PER_FIELD;

	// Handle non-array results
	if (!Array.isArray(data)) {
		// Single value result (e.g., COUNT(*))
		if (data !== null && data !== undefined) {
			return {
				summary: {
					totalRecords: 1,
					recordsShown: 1,
					schema: [{ name: 'result', type: inferFieldType(data) }]
				},
				data: [{ result: data }],
				truncationNote: null
			};
		}

		return {
			summary: {
				totalRecords: 0,
				recordsShown: 0,
				schema: []
			},
			data: [],
			truncationNote: null
		};
	}

	// Process array data
	const {
		data: truncatedData,
		totalRecords,
		recordsShown,
		truncatedFields
	} = truncateDataArray(data as Record<string, unknown>[], options);

	const schema = extractSchema(truncatedData, truncatedFields);
	const truncationNote = buildTruncationNote(
		totalRecords,
		recordsShown,
		truncatedFields,
		maxCharsPerField,
		'query'
	);

	return {
		summary: {
			totalRecords,
			recordsShown,
			schema
		},
		data: truncatedData,
		truncationNote
	};
}

/**
 * Formats external tool result for LLM consumption
 * @param result - Raw tool result
 * @param options - Formatting options including tool-specific limits
 * @returns Formatted result structure for LLM
 */
export function formatToolResultForLLM(
	result: unknown,
	options: FormatToolResultOptions = {}
): FormattedResultForLLM & { toolName?: string } {
	const { toolName, toolLimit } = options;

	// Use tool's limit if specified, otherwise use default
	const effectiveMaxRows = toolLimit ?? options.maxRows ?? DEFAULT_MAX_ROWS;
	const maxCharsPerField = options.maxCharsPerField ?? DEFAULT_MAX_CHARS_PER_FIELD;

	// Handle null/undefined result
	if (result === null || result === undefined) {
		return {
			toolName,
			summary: {
				totalRecords: 0,
				recordsShown: 0,
				schema: []
			},
			data: [],
			truncationNote: null
		};
	}

	// Handle string result
	if (typeof result === 'string') {
		const { text, wasTruncated } = truncateTextField(result, maxCharsPerField);
		return {
			toolName,
			summary: {
				totalRecords: 1,
				recordsShown: 1,
				schema: [{ name: 'result', type: 'string', truncated: wasTruncated || undefined }]
			},
			data: [{ result: text }],
			truncationNote: wasTruncated ? `Result truncated to ${maxCharsPerField} chars.` : null
		};
	}

	// Handle array result (most common for external tools)
	if (Array.isArray(result)) {
		const {
			data: truncatedData,
			totalRecords,
			recordsShown,
			truncatedFields
		} = truncateDataArray(result as Record<string, unknown>[], {
			maxRows: effectiveMaxRows,
			maxCharsPerField
		});

		const schema = extractSchema(truncatedData, truncatedFields);
		const truncationNote = buildTruncationNote(
			totalRecords,
			recordsShown,
			truncatedFields,
			maxCharsPerField,
			toolName
		);

		return {
			toolName,
			summary: {
				totalRecords,
				recordsShown,
				schema
			},
			data: truncatedData,
			truncationNote
		};
	}

	// Handle object result - check for common wrapper patterns
	if (typeof result === 'object') {
		const objResult = result as Record<string, unknown>;

		// Check for common data wrapper patterns: { data: [...] }, { results: [...] }, { items: [...] }
		const dataWrapperKeys = ['data', 'results', 'items', 'records', 'rows', 'list'];
		for (const key of dataWrapperKeys) {
			if (Array.isArray(objResult[key])) {
				const innerData = objResult[key] as Record<string, unknown>[];
				const {
					data: truncatedData,
					totalRecords,
					recordsShown,
					truncatedFields: dataTruncatedFields
				} = truncateDataArray(innerData, {
					maxRows: effectiveMaxRows,
					maxCharsPerField
				});

				// Extract metadata (non-array top-level properties like totalItems, totalDeadstockItems)
				const {
					metadata,
					truncatedFields: metadataTruncatedFields
				} = extractMetadataFromObject(objResult, key, maxCharsPerField);

				// Merge truncated fields from both data and metadata
				const allTruncatedFields = new Set([...dataTruncatedFields, ...metadataTruncatedFields]);

				const schema = extractSchema(truncatedData, dataTruncatedFields);
				const truncationNote = buildTruncationNote(
					totalRecords,
					recordsShown,
					allTruncatedFields,
					maxCharsPerField,
					toolName
				);

				// Only include metadata if there are non-array properties
				const hasMetadata = Object.keys(metadata).length > 0;

				return {
					toolName,
					summary: {
						totalRecords,
						recordsShown,
						schema
					},
					...(hasMetadata && { metadata }),
					data: truncatedData,
					truncationNote
				};
			}
		}

		// Single object result - wrap in array and truncate fields
		const { row: truncatedRow, truncatedFields } = truncateRow(objResult, maxCharsPerField);
		const schema = extractSchema([truncatedRow], truncatedFields);
		const truncationNote = truncatedFields.size > 0
			? `Fields truncated to ${maxCharsPerField} chars: ${Array.from(truncatedFields).join(', ')}.`
			: null;

		return {
			toolName,
			summary: {
				totalRecords: 1,
				recordsShown: 1,
				schema
			},
			data: [truncatedRow],
			truncationNote
		};
	}

	// Fallback for other types
	return {
		toolName,
		summary: {
			totalRecords: 1,
			recordsShown: 1,
			schema: [{ name: 'result', type: inferFieldType(result) }]
		},
		data: [{ result }],
		truncationNote: null
	};
}

/**
 * Converts formatted result to a string for LLM consumption
 * @param formattedResult - The formatted result object
 * @returns JSON string representation
 */
export function formatResultAsString(formattedResult: FormattedResultForLLM): string {
	return JSON.stringify(formattedResult, null, 2);
}
